/**
 * The rules the shared-room occupants UI has to obey, kept out of the components
 * so they can be tested without a render harness.
 *
 * Three of them are not obvious from the screen:
 *
 *  1. A guest already in the room must never appear in the "add someone" search.
 *     The endpoint answers 409 already_an_occupant, and a picker that offers a
 *     name it cannot add reads as a broken button.
 *  2. The primary badge only ever MOVES. There is no demote control, because the
 *     API rejects `isPrimary: false` and the database refuses a room that has
 *     occupants and no primary — a room in that state quotes its rate to nobody,
 *     or invites a later "promote whoever is left" guess that hands the payer's
 *     rate to a roommate.
 *  3. Occupant count is not head count. Adding a name never moves party_size, so
 *     a room whose named list would outgrow it is a clash the host resolves
 *     explicitly, not something Studio quietly corrects.
 */
import { normalizeName } from './invitee-match';
import type { GroupInvitee, GroupRoomOccupant } from '../types/groups';

/**
 * Anything already holding the seat this picker is filling: a room occupant, or
 * a dinner claim.
 *
 * Widened from `GroupRoomOccupant` so the dinner-table picker reuses this exact
 * fold instead of growing a second one. A search that folds differently from the
 * import that created the row simply never finds the guest, and one copy of that
 * rule is the only way to be sure both pickers agree.
 *
 * `inviteeId` is nullable because a dinner claim can be a bare guest name with
 * no roster row behind it. Such a claim excludes nobody, which is correct — there
 * is no id to match it against a roster entry, and inventing one by name is the
 * merge this codebase keeps refusing to make.
 */
export interface SeatHolder {
  inviteeId?: string | null;
}

/**
 * The roster minus everyone already in the room, narrowed by what the host
 * typed. A blank query lists the whole eligible roster, which is what makes this
 * a picker rather than a guessing game on a trip whose guests share surnames.
 *
 * Folds with the importer's `normalizeName` on purpose: rosters pasted out of
 * Partiful carry non-breaking spaces and curly quotes, and a search that folds
 * differently from the import that created the row simply never finds them.
 */
export function searchRoster(
  roster: readonly GroupInvitee[],
  occupants: readonly SeatHolder[],
  query: string,
): GroupInvitee[] {
  const inRoom = new Set(occupants.map((occupant) => occupant.inviteeId));
  const eligible = roster.filter((invitee) => !inRoom.has(invitee.id));

  const needle = normalizeName(query);
  if (!needle) return eligible;

  const digits = needle.replace(/\D/g, '');
  return eligible.filter((invitee) => {
    const haystack = normalizeName(
      [invitee.fullName, invitee.email, invitee.phone].filter(Boolean).join(' '),
    );
    if (haystack.includes(needle)) return true;

    // Hosts type a number the way they remember it; the roster stores it the way
    // it was imported. Compare digits only so "(555) 010-0000" finds "+15550100000".
    return digits.length >= 3 && (invitee.phone || '').replace(/\D/g, '').includes(digits);
  });
}

/**
 * What the picker says when it has nobody to offer.
 *
 * Three different situations produce the same empty list and they are not the
 * same fact. "Everyone on the roster is already in this room" is a claim about
 * a roster, and on a trip that has no guests yet it is simply untrue: the host
 * imported nobody, so nobody is in the room either. Reservations arrive from the
 * guest page without creating an invitee, so a group can genuinely hold room
 * requests and an empty roster — that host opens this dialog, is told the room
 * is already full of people it does not list, and has no idea the fix is to
 * import the roster.
 *
 * Lives here rather than inline in the component for the usual reason: there is
 * no render harness in this package, so the wording is unit-tested and the
 * component only chooses when to show it.
 */
export function occupantSearchEmptyNote(
  roster: readonly GroupInvitee[],
  query: string,
): string {
  const typed = query.trim();
  if (typed) return `Nobody on this trip matches “${typed}”.`;
  // Vacuously "everyone", and the one phrasing a host cannot act on.
  if (roster.length === 0) {
    return 'No guests on this trip yet — add the first one below, or import the roster on the Guests tab.';
  }
  return 'Everyone on the roster is already in this room.';
}

export interface OccupantControls {
  /**
   * Offer "Make primary". Never true for the current primary: you replace a
   * primary by promoting someone else, and you can never empty the seat.
   */
  canPromote: boolean;
  canRemove: boolean;
  /** Why remove is refused, in words a host can act on. '' when it is allowed. */
  removeBlockedReason: string;
}

/**
 * What a host may do to one occupant, given who else is in the room.
 *
 * Removing a lone primary IS allowed — that un-links a reservation attached to
 * the wrong guest, and leaves the same unassigned state a reservation has before
 * anyone is named. Removing a primary who has roommates is not: it would strand
 * a room full of people with nobody holding the booking.
 */
export function occupantControls(
  occupants: GroupRoomOccupant[],
  occupant: GroupRoomOccupant,
): OccupantControls {
  const others = occupants.filter((other) => other.id !== occupant.id);
  const strandsRoommates = occupant.isPrimary && others.length > 0;

  return {
    canPromote: !occupant.isPrimary,
    canRemove: !strandsRoommates,
    removeBlockedReason: strandsRoommates
      ? `${occupant.fullName} is the primary on this room. Make someone else primary first — a room with people in it and no primary shows its rate to nobody.`
      : '',
  };
}

export interface PartySizeConflict {
  /** Travellers the room is currently set for. */
  partySize: number;
  /** Named occupants it already has. */
  namedOccupantCount: number;
  /** What to raise the room to so the person being added fits. */
  suggestedPartySize: number;
}

/**
 * Read the 409 that fires when a named list would outgrow the head count.
 *
 * Returns null for every other failure, so the caller shows the one-click raise
 * only when raising is actually the fix. The API sends both numbers precisely so
 * Studio can offer it without a second round trip; if either is missing we fall
 * back to the plain error rather than guessing a head count for a hotel.
 */
export function partySizeConflict(error: unknown): PartySizeConflict | null {
  const body = (error as { response?: { data?: Record<string, unknown> } })?.response?.data;
  if (!body || typeof body !== 'object') return null;
  if (body.error !== 'occupant_count_exceeds_party_size') return null;

  const partySize = Number(body.partySize);
  const namedOccupantCount = Number(body.namedOccupantCount);
  if (!Number.isFinite(partySize) || !Number.isFinite(namedOccupantCount)) return null;

  return {
    partySize,
    namedOccupantCount,
    suggestedPartySize: Math.max(partySize, namedOccupantCount) + 1,
  };
}
